import { Label } from "@/components/ui/field";
import { IconButton } from "@/components/ui/icon-button";
import { Popover } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  CaretDownIcon,
  CircleNotchIcon,
  MagnifyingGlassIcon,
  XCircleIcon,
} from "@phosphor-icons/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Input as AriaInput,
  ComboBox,
  ListBox,
  composeRenderProps,
  useFilter,
} from "react-aria-components";
import type {
  AutocompleteGroup,
  AutocompleteOption,
  AutocompleteProps,
} from "./Autocomplete.type";
import { EmptyState, ListContent } from "./AutocompleteListContent";
import { createDisplayStructure, createItems } from "./autocompleteHelpers";

function Autocomplete<T = AutocompleteOption, TValue = string>({
  options,
  label,
  placeholder = "Type to search or select an option",
  focusText,
  noneOptionText = "None",
  defaultToNone = true,
  value,
  defaultValue,
  disabled = false,
  isPending = false,
  clearable = false,
  className,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledby,
  getOptionLabel = (option: T) => (option as AutocompleteOption).label,
  getOptionValue = (option: T) =>
    (option as AutocompleteOption).value as TValue,
  getGroupLabel = (group: AutocompleteGroup<T>) => group.label,
  onValueChange,
  onSearchChange,
  emptyMessage = "No results found.",
  labelVariant = "default",
  labelSize = "md",
  menuTrigger = "focus",
}: AutocompleteProps<T, TValue>) {
  const [inputValue, setInputValue] = useState("");
  const [selectedKey, setSelectedKey] = useState<string | null>(
    value ? String(value) : defaultValue ? String(defaultValue) : null,
  );
  const [isFocused, setIsFocused] = useState(false);
  const [showAllOptions, setShowAllOptions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [popoverWidth, setPopoverWidth] = useState<number | undefined>();

  // Sync popover width with wrapper width
  useEffect(() => {
    if (wrapperRef.current && isFocused) {
      const updateWidth = () => {
        if (wrapperRef.current) {
          setPopoverWidth(wrapperRef.current.getBoundingClientRect().width);
        }
      };
      updateWidth();
      window.addEventListener("resize", updateWidth);
      return () => window.removeEventListener("resize", updateWidth);
    }
  }, [isFocused]);

  // Initialize useFilter hook
  const { contains } = useFilter({ sensitivity: "base" });

  // Stabilize helper functions to prevent unnecessary re-renders
  const stableGetOptionLabel = useCallback(getOptionLabel, [getOptionLabel]);
  const stableGetOptionValue = useCallback(getOptionValue, [getOptionValue]);
  const stableGetGroupLabel = useCallback(getGroupLabel, [getGroupLabel]);

  // Create items for the combobox
  const items = useMemo(
    () =>
      createItems(
        options,
        noneOptionText,
        stableGetOptionLabel,
        stableGetOptionValue,
        stableGetGroupLabel,
        defaultToNone,
      ),
    [
      options,
      noneOptionText,
      defaultToNone,
      stableGetOptionLabel,
      stableGetOptionValue,
      stableGetGroupLabel,
    ],
  );

  // Filter items based on input value
  // When showAllOptions is true (opened via chevron), show all items regardless of inputValue
  const filteredItems = useMemo(() => {
    if (showAllOptions) return items;
    if (!inputValue) return items;
    return items.filter(
      (item) => !item.isNoneOption && contains(item.label, inputValue),
    );
  }, [items, inputValue, contains, showAllOptions]);

  // Group filtered items for display
  const displayStructure = useMemo(
    () => createDisplayStructure(filteredItems),
    [filteredItems],
  );

  // Check if there are any actual options (excluding the none option)
  const hasActualOptions = useMemo(
    () => filteredItems.some((item) => !item.isNoneOption),
    [filteredItems],
  );

  // Compute the placeholder text based on focus state
  const currentPlaceholder = useMemo(() => {
    if (isFocused && focusText !== undefined) {
      return focusText;
    }
    return placeholder;
  }, [isFocused, focusText, placeholder]);

  // Sync internal state with value prop changes - only when value actually changes
  useEffect(() => {
    const newSelectedKey = value ? String(value) : null;
    setSelectedKey(newSelectedKey);

    // Only update input value if this is a programmatic change (not user typing)
    if (!value) {
      setInputValue("");
    } else {
      // Find the corresponding item to get its label
      const correspondingItem = items.find(
        (item) => item.value === value && !item.isNoneOption,
      );
      if (correspondingItem) {
        setInputValue(correspondingItem.label);
      }
    }
  }, [value]);

  // Event handlers
  const handleSelectionChange = (key: string | number | null) => {
    const stringKey = key ? String(key) : null;
    setSelectedKey(stringKey);

    if (stringKey === "__none__" || stringKey === null) {
      setInputValue("");
      onValueChange?.(null, noneOptionText);
    } else {
      const selectedItem = items.find((item) => item.id === stringKey);
      if (selectedItem && !selectedItem.isNoneOption) {
        setInputValue(selectedItem.label);
        onValueChange?.(selectedItem.value, selectedItem.label);
      }
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    // When focusing via input click, clear showAllOptions flag so filtering works
    setShowAllOptions(false);
    // The ComboBox with menuTrigger="focus" should open automatically when input receives focus
  };
  const handleBlur = () => {
    setIsFocused(false);
    setShowAllOptions(false);
    // Restore selected value's label when blurring if user didn't type anything
    if (selectedKey && !inputValue) {
      const selectedItem = items.find(
        (item) => item.id === selectedKey && !item.isNoneOption,
      );
      if (selectedItem) {
        setInputValue(selectedItem.label);
      }
    }
  };

  const handleChevronClick = () => {
    if (disabled) return;

    // Set flag to show all options when chevron is clicked
    // This keeps the selected value visible but shows all options in dropdown
    setShowAllOptions(true);

    // Focus the input to open the dropdown
    inputRef.current?.focus();
  };

  const handleInputChange = (newValue: string) => {
    setInputValue(newValue);
    // When user types, disable showAllOptions so filtering works
    setShowAllOptions(false);
    if (newValue === "") {
      onValueChange?.(null, noneOptionText);
    } else {
      onSearchChange?.(newValue);
    }
  };

  // Determine accessibility attributes
  const inputAriaProps = {
    "aria-label": !ariaLabelledby ? ariaLabel || "Select an option" : undefined,
    "aria-labelledby": ariaLabelledby,
  };

  return (
    <div className={cn("relative", className)}>
      {label && (
        <Label variant={labelVariant} size={labelSize}>
          {label}
        </Label>
      )}

      <ComboBox
        selectedKey={selectedKey}
        onSelectionChange={handleSelectionChange}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        isDisabled={disabled}
        menuTrigger={menuTrigger}
        {...inputAriaProps}
        className={cn(
          "focus:outline-hidden focus-visible:outline-hidden",
          "data-[focus-visible=true]:ring-0!",
          "data-[focus-visible=true]:ring-offset-0!",
          "data-[focused=true]:ring-0!",
          "data-[focused=true]:ring-offset-0!",
        )}
      >
        <div ref={wrapperRef} className="relative">
          {isFocused && (
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400 z-10" />
          )}

          <AriaInput
            ref={inputRef}
            placeholder={currentPlaceholder}
            value={inputValue}
            onChange={(e) => handleInputChange(e.target.value)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={disabled}
            className={composeRenderProps(
              cn(
                "flex w-full rounded-md border border-gray-200 bg-white",
                "ring-offset-white",
                "text-xs",
                "placeholder:text-gray-500",
                isFocused ? "pl-10" : "px-3",
                clearable && inputValue ? "pr-9" : "pr-8",
                "data-[focus-visible=true]:ring-0!",
                "data-[focus-visible=true]:ring-offset-0!",
                "data-[focused=true]:ring-0!",
                "data-[focused=true]:ring-offset-0!",
                "data-[focused=true]:border-blue-600",
                "focus:outline-hidden focus-visible:outline-hidden",
                "text-ellipsis overflow-hidden whitespace-nowrap",
                "data-disabled:opacity-50",
                "h-9 py-2",
              ),
              (className) => className,
            )}
            {...inputAriaProps}
          />

          {clearable && inputValue && (
            <div
              className="absolute right-8 top-1/2 -translate-y-1/2"
              aria-hidden="true"
            >
              <IconButton
                icon={
                  <XCircleIcon
                    weight="fill"
                    className="text-gray-400 hover:text-gray-600 w-4 h-4"
                  />
                }
                aria-label="Clear text"
                variant="icon-only"
                excludeFromTabOrder
                onPressChange={() => {
                  handleInputChange("");
                  onValueChange?.(null, noneOptionText);
                }}
              />
            </div>
          )}

          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {isPending && (
              <CircleNotchIcon className="size-4 animate-spin text-gray-400" />
            )}

            <IconButton
              variant="icon-only"
              icon={<CaretDownIcon className="size-4" />}
              aria-label="Toggle dropdown"
              className={cn("text-gray-400 hover:text-gray-600")}
              onPress={handleChevronClick}
            />
          </div>
        </div>

        <Popover
          triggerRef={inputRef}
          className={cn(
            "p-1",
            "max-h-[200px] overflow-auto",
            "z-50 bg-white border border-gray-200 rounded-md shadow-lg",
          )}
          style={popoverWidth ? { width: `${popoverWidth}px` } : undefined}
          placement="bottom"
          shouldFlip={true}
          containerPadding={12}
          crossOffset={0}
          isNonModal={true}
        >
          <ListBox
            className={cn(
              "outline-hidden",
              "max-h-full overflow-y-auto",
              "focus:outline-hidden",
            )}
          >
            {!hasActualOptions ||
            (inputValue &&
              !filteredItems.some((item) => !item.isNoneOption)) ? (
              <EmptyState
                emptyMessage={emptyMessage}
                noneOption={items.find((item) => item.isNoneOption) ?? null}
                selectedKey={selectedKey}
              />
            ) : (
              <ListContent
                displayStructure={displayStructure}
                selectedKey={selectedKey}
              />
            )}
          </ListBox>
        </Popover>
      </ComboBox>
    </div>
  );
}

export default Autocomplete;
Autocomplete.displayName = "Autocomplete";
